import { useMemo, useState } from "react";
import { allCampaigns, campaignCategories, simulationNote } from "@/mocks/campaigns";
import { buildWhatsAppLink } from "@/utils/whatsapp";
import Reveal from "./Reveal";
import SectionHeading from "./SectionHeading";

interface CampaignItem {
  id: string;
  category: string;
  title: string;
  price: string;
  subPrice?: string;
  subPrice2?: string;
  subtitle?: string;
  icon: string;
  image?: string;
  extraImages?: string[];
  points?: string[];
  note?: string;
  buttonLabel?: string;
  message: string;
}

function CampaignCard({ item }: { item: CampaignItem }) {
  const longPrice = item.price.length > 26;

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-xl border border-background-300/70 bg-background-50 transition-transform duration-300 hover:-translate-y-1">
      {item.extraImages ? (
        <div className="grid grid-cols-3 gap-px bg-primary-900">
          {item.extraImages.map((src) => (
            <div key={src} className="bg-primary-900">
              <img
                src={src}
                alt={`${item.title} cilt bakımı uygulaması`}
                title={`${item.title} - Fatma Angay Güzellik Salonu`}
                className="h-auto w-full"
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="emerald-canvas flex h-32 items-center justify-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full border border-accent-500/60 bg-primary-950/40">
            <i className={`${item.icon} text-3xl text-accent-400`} aria-hidden="true" />
          </span>
        </div>
      )}

      <div className="flex flex-1 flex-col p-6">
        <span className="font-label text-[0.6rem] font-semibold uppercase tracking-[0.24em] text-secondary-600">
          {item.category}
        </span>

        <h3 className="mt-3 font-heading text-lg font-semibold uppercase leading-tight tracking-tight text-foreground-950 md:text-xl">
          {item.title}
        </h3>

        {item.subtitle ? (
          <p className="mt-1.5 font-body text-xs text-foreground-600 md:text-sm">{item.subtitle}</p>
        ) : null}

        <p
          className={`mt-4 font-heading font-semibold tracking-tight text-accent-700 ${
            longPrice ? "text-sm leading-relaxed md:text-base" : "text-2xl md:text-[1.7rem]"
          }`}
        >
          {item.price}
        </p>

        {item.subPrice ? (
          <p className="mt-1 font-body text-xs text-foreground-600 md:text-sm">{item.subPrice}</p>
        ) : null}
        {item.subPrice2 ? (
          <p className="font-body text-xs text-foreground-600 md:text-sm">{item.subPrice2}</p>
        ) : null}

        {item.points ? (
          <ul className="mt-5 grid grid-cols-1 gap-2 border-t border-background-200 pt-5">
            {item.points.map((point) => (
              <li key={point} className="flex items-start gap-2">
                <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center">
                  <i className="ri-check-line text-sm text-primary-500" aria-hidden="true" />
                </span>
                <span className="font-body text-xs leading-relaxed text-foreground-700 md:text-sm">
                  {point}
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {item.note ? (
          <p className="mt-4 rounded-lg bg-secondary-50 px-3 py-2.5 font-body text-xs leading-relaxed text-secondary-800">
            {item.note}
          </p>
        ) : null}

        <a
          href={buildWhatsAppLink(item.message)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-600 px-5 py-3 font-label text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-primary-700 transition-colors hover:bg-primary-600 hover:text-background-50 md:text-xs"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-whatsapp-line text-base" aria-hidden="true" />
          </span>
          {item.buttonLabel ?? "Detaylı Bilgi Al"}
        </a>
      </div>
    </article>
  );
}

export default function AllCampaigns() {
  const [active, setActive] = useState("Tümü");

  const filtered = useMemo(
    () => (active === "Tümü" ? allCampaigns : allCampaigns.filter((item) => item.category === active)),
    [active]
  );

  return (
    <section id="tum-kampanyalar" className="bg-background-100 py-16 md:py-24">
      <div className="mx-auto w-full max-w-7xl px-5 md:px-6">
        <Reveal>
          <SectionHeading
            eyebrow="Tüm Kampanyalar"
            title="Bakım Seçenekleri ve Kampanya Fiyatları"
            description="İlgilendiğiniz kategoriyi seçin, kampanya detaylarını inceleyin ve dilediğiniz uygulama için WhatsApp üzerinden bilgi alın."
          />
        </Reveal>

        <Reveal delay={80}>
          <div className="no-scrollbar mt-10 flex w-full gap-2 overflow-x-auto pb-1 md:flex-wrap md:justify-center md:overflow-visible">
            {campaignCategories.map((category) => {
              const isActive = category === active;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActive(category)}
                  aria-pressed={isActive}
                  className={`flex-shrink-0 cursor-pointer whitespace-nowrap rounded-full border px-5 py-2.5 font-label text-xs font-medium tracking-wide transition-colors md:text-sm ${
                    isActive
                      ? "border-primary-600 bg-primary-600 text-background-50"
                      : "border-background-300 bg-background-50 text-foreground-700 hover:border-accent-400 hover:text-accent-700"
                  }`}
                >
                  {category}
                </button>
              );
            })}
          </div>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((item, index) => (
            <Reveal key={item.id} delay={(index % 3) * 90} className="h-full">
              <CampaignCard item={item as CampaignItem} />
            </Reveal>
          ))}
        </div>

        <p className="mt-8 text-center font-body text-xs leading-relaxed text-foreground-600 md:text-sm">
          {simulationNote}
        </p>
      </div>
    </section>
  );
}